/**
 * Add to Cart component
 * 
 * @module component-addtocart
 * @version 2.0.0
 * @extends HTMLElement
 */
 class addToCartComponent extends HTMLElement {

    /**
     * Add to Cart web component.
     * 
     * @constructor
     */
    constructor() {
        super();
        
        // Flag to track if listeners have been added
        this._listenersAdded = false;

        if (this.dataset.handle) {
            window.theme.product.loadProduct(this.dataset.handle, true).then(product => {
                this.initSubmitButton(product);
                this.initEvents(product);
            });
        } else {
            console.log('Missing product handle', this);
        }
    }

    /**
     * Init component events.
     * 
     * @param {object} product Product data.
     * @returns {undefined}
     */
    initEvents(product) {
        // Skip if listeners already added
        if (this._listenersAdded) {
            return;
        }
        
        // Mark listeners as added
        this._listenersAdded = true;

        // On submit add to cart form
        const form = this.getForm();
        if (form) {
            form.addEventListener('submit', event => this.onFormSubmit(event));
        }

        // On variant selector ready
        const variantSelector = document.querySelector('variant-selector[data-handle="'+this.dataset.handle+'"]');
        if (variantSelector) {
            variantSelector.addEventListener('ready', event => this.OnVariantSelectorReady(event, product));

            if (variantSelector.classList.contains('ready')) {
                this.OnVariantSelectorChange(variantSelector.querySelector('select'), product);
            }
        }

        // Check initial inventory status if in quick view
        if (this.closest('component-quickview') && window.productsInventoryQuantity) {
            const variant = this.getSelectedVariant(product);
            if (variant) {
                this.checkInventoryStatus(variant);
            }
        }
        
        // Listen for cart updated events
        document.addEventListener('cart:updated', () => {
            const variant = this.getSelectedVariant(product);
            if (variant) {
                this.checkInventoryStatus(variant);
            }
        });
    }

    /**
     * Get selected variant from the form
     * 
     * @param {object} product Product data.
     * @returns {object} The selected variant.
     */
    getSelectedVariant(product) {
        const variantSelector = document.querySelector('variant-selector[data-handle="'+this.dataset.handle+'"] select');
        if (!variantSelector) return null;
        
        const variantId = variantSelector.value;
        return product.variants.find(v => v.id == variantId);
    }

    /**
     * Reset inventory UI elements
     */
    resetInventoryUI() {
        // Find the parent container (quickview or product page)
        const container = this.closest("component-quickview") || document;
        
        // Reset UI elements
        const inStockEl = container.querySelector('.js-in-stock');
        const allAddedEl = container.querySelector('.js-in-stock-all-added');
        const btn = this.getSubmitButton();
        
        if (inStockEl) inStockEl.classList.add('hidden');
        if (allAddedEl) allAddedEl.classList.add('hidden');
        
        if (btn) {
            btn.removeAttribute('disabled');
            btn.classList.remove('disabled');
        }
    }

    /**
     * Check inventory status and update button state
     * 
     * @param {object} variant Current variant data.
     * @returns {undefined}
     */
    async checkInventoryStatus(variant) {
        if (!window.productsInventoryQuantity || !variant) return;
        
        try {
            // Get fresh cart data
            const req = await fetch(window.Shopify.routes.root + 'cart.js');
            const res = await req.json();
            const variantInCart = res.items.find(item => item.variant_id === variant.id);
            const btn = this.getSubmitButton();
            
            if (!btn) return;

            // Parse inventory data
            const resultArray = window.productsInventoryQuantity.map(item => {
                const [key, value] = item.split(':');
                return { [key]: parseInt(value) };
            });

            const productVariantQuantity = resultArray.find(obj => obj.hasOwnProperty(variant.id));
            
            if (!productVariantQuantity) return;
            
            const inventoryQuantity = productVariantQuantity[variant.id];
            const variantInCartQty = variantInCart ? variantInCart.quantity : 0;
            
            // Find the parent container (quickview or product page)
            const container = this.closest("component-quickview") || document;
            const inStockItem = container.querySelector('.js-in-stock');
            const allAddedItem = container.querySelector('.js-in-stock-all-added');
            
            // Reset UI first
            if (inStockItem) inStockItem.classList.add('hidden');
            if (allAddedItem) allAddedItem.classList.add('hidden');
            
            // Enable the button by default
            btn.removeAttribute('disabled');
            btn.classList.remove('disabled');
            
            // Check if all inventory is in cart
            if (variantInCartQty > 0 && variantInCartQty >= inventoryQuantity) {
                btn.classList.add('disabled');
                
                // Show the "All items have been added to your cart" message
                if (allAddedItem) {
                    allAddedItem.classList.remove('hidden');
                }
            } else if (inventoryQuantity > 0 && inventoryQuantity <= 10) {
                // Show low stock warning
                const remainingStock = inventoryQuantity - variantInCartQty;
                
                if (remainingStock > 0 && inStockItem) {
                    const inStockTitle = inStockItem.querySelector('.js-in-stock-title');
                    const inStockQuantity = inStockItem.querySelector('.js-in-stock-quantity');
                    
                    if (inStockTitle && inStockQuantity) {
                        inStockTitle.innerHTML = variant.option1;
                        inStockQuantity.innerHTML = remainingStock;
                        inStockItem.classList.remove('hidden');
                    }
                }
            }
        } catch (error) {
            console.error("Error checking inventory status:", error);
        }
    }

    /**
    * Toggle button if product select variant is out of stock.
    * 
    * @param {object} select Product select variant.
    * @param {object} product Product data.
    * @param {boolean} isSizeSelected Whether size is selected.
    * @returns {undefined}
    */
    toggleButton(select, product, isSizeSelected) {
        let optionSelected = select.children[select.selectedIndex],
            outOfStockContent = this.querySelector('[data-outofstock-content]'),
            btn = this.getSubmitButton(),
            sizeNotSelectedBtn = this.querySelector('.js-product-form__buttons-size-selected'),
            sizeSelectedBtn = this.querySelector('.js-product-form__buttons');

        if (isSizeSelected) {
            if (sizeNotSelectedBtn) sizeNotSelectedBtn.classList.add('hidden');
            if (sizeSelectedBtn) sizeSelectedBtn.classList.remove('hidden');
        } else {
            if (sizeNotSelectedBtn) sizeNotSelectedBtn.classList.remove('hidden');
            if (sizeSelectedBtn) sizeSelectedBtn.classList.add('hidden');
            return;
        }
            
        if (optionSelected) {
            if (optionSelected.dataset.disabled === '') {
                optionSelected = false;
            } else {
                optionSelected = true;
            }
        } else {
            optionSelected = false;
        }

        if (optionSelected) {
            outOfStockContent.classList.remove('active');
            btn.disabled = false;
        } else {
            outOfStockContent.classList.add('active');
            btn.disabled = true;
        }
    }

    /**
     * Get component template
     * 
     * @returns {string} Component HTML template.
     */
    getTemplate() {
        let addToCartLabel = 'Add to Bag',
            soldOutLabel = 'Sold Out';

        if(this.dataset.soldOutLabel) {
            soldOutLabel = this.dataset.soldOutLabel;
        }
        
        if(this.dataset.addToCartLabel) {
            addToCartLabel = this.dataset.addToCartLabel;
        }

        let templateHTML = '<div class="product-addtocart__button--out-of-stock hidden" data-outofstock-content>' + soldOutLabel + '</div>';

        templateHTML += '<div class="product-addtocart__button--add-to-cart-content flex align-center justify-center" data-add-to-card-content>'
                        + '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-loading !hidden" width="26" height="26" viewBox="25 25 50 50" fill="none" xmlns="http://www.w3.org/2000/svg"><path stroke="none" d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform></path></svg>'
                        + '<span class="inline-block">'+ addToCartLabel + '</span>'
                        + '</div>';

        return templateHTML;
    }

    /**
     * Init add to cart button features
     * 
     * @param {object} product Product data.
     * @returns {undefined}
     */
    initSubmitButton(product) {
        let html = this.getTemplate(),
            btn  = this.getSubmitButton();

        if (html && btn) {
            btn.classList.add('product-addtocart__button');
            btn.classList.add('btn-icon');
            btn.classList.add('btn-icon-left');
            btn.classList.add('btn-loading');

            if (product.available) {
                btn.classList.add('active');
            } else {
                btn.disabled = true;
            }

            // Inject HTML
            btn.innerHTML = html;
        }
    }

    getForm() {
        return this.querySelector('form');
    }

    getSubmitButton() {
        return this.querySelector('button[type="submit"]');
    }

    onFormSubmit(event) {
        const form = event.target;
        event.preventDefault();
    
        let button   = form.querySelector('[type="submit"]'),
            validate = this.validateFieldRequired(form),
            message  = form.querySelector('[data-error-message]');

        if (!validate) {
            button.setAttribute('disabled', true);
            
            window.theme.cart.addByForm(form).then(result => {
                button.removeAttribute('disabled');
                message.innerHTML = '';
                message.classList.remove("enabled");

                // Check inventory status after adding to cart
                const product = window.theme.product.getLoadedProduct(this.dataset.handle);
                if (product) {
                    const variant = this.getSelectedVariant(product);
                    if (variant) {
                        this.checkInventoryStatus(variant);
                    }
                }

                // Dispatch cart updated event
                document.dispatchEvent(new CustomEvent('cart:updated'));
            }).catch(error => {
                console.log(error.message);
                button.removeAttribute('disabled');
                
                message.innerHTML = '';
                message.append(error.message);
                message.classList.add("enabled");
            });
        }
    }

    OnVariantSelectorReady(event, product) {
        let select = event.target.querySelector('select');
        this.OnVariantSelectorChange(select, product);
    }

    OnVariantSelectorChange(select, product) {
        const productAssociation = document.querySelector('product-association[data-handle="'+this.dataset.handle+'"]');
        let isSizeSelected = false;
        
        if (productAssociation) {
            const allOptions = productAssociation.querySelectorAll('[data-option]');
            for (let option of allOptions) {
                const optionName = option.getAttribute('data-option');
                if (optionName && optionName.toLowerCase().includes('size')) {
                    const sizeInput = option.querySelector('input[data-swatch-input]:checked');
                    isSizeSelected = sizeInput !== null;
                    break;
                }
            }
        }
        
        this.toggleButton(select, product, isSizeSelected);

        // Skip if the change listener was already added
        if (select._changeListenerAdded) {
            return;
        }
        
        // Mark as listener added
        select._changeListenerAdded = true;

        // Handle variant changes
        select.addEventListener('change', event => {
            // Immediately reset UI state
            this.resetInventoryUI();
            
            const isSizeSelected = event.detail.isSizeSelected;
            this.toggleButton(event.target, product, isSizeSelected);
            
            // Use a short delay to ensure DOM updates are complete
            setTimeout(() => {
                try {
                    // Check inventory status after variant change
                    if (window.productsInventoryQuantity && isSizeSelected) {
                        const variant = this.getSelectedVariant(product);
                        if (variant) {
                            this.checkInventoryStatus(variant);
                        }
                    }
                } catch (error) {
                    console.error("Error during variant change:", error);
                }
            }, 150);
        });
    }

    validateFieldRequired(form) {
		const fields = form.querySelectorAll("[required]");
		let foundError = false;

		for (let field of fields) {
			foundError = field.validity.valueMissing;

			if (foundError) {
				field.classList.add("invalid");
			}
		}

		return foundError;
	}
}

customElements.define('component-addtocart', addToCartComponent);
